import WashdayClientSingleton from './WashdayClientSingleton';
import type { AttendanceClientMetadata, AttendanceLocationMetadata } from 'washday-sdk';

export type { AttendanceClientMetadata, AttendanceLocationMetadata } from 'washday-sdk';

const ATTENDANCE_DEVICE_ID_KEY = 'washday:attendanceDeviceId';

const optionalNumber = (value: number | null): number | undefined =>
  typeof value === 'number' && Number.isFinite(value) ? value : undefined;

const omitUndefined = <T extends Record<string, unknown>>(value: T): T =>
  Object.fromEntries(Object.entries(value).filter(([, item]) => item !== undefined)) as T;

const createAttendanceDeviceId = (): string => {
  const cryptoObj = globalThis.crypto;

  if (typeof cryptoObj?.randomUUID === 'function') {
    return cryptoObj.randomUUID();
  }

  if (typeof cryptoObj?.getRandomValues === 'function') {
    const bytes = cryptoObj.getRandomValues(new Uint8Array(16));
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex
      .slice(6, 8)
      .join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
  }

  return `attendance-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

export const getAttendanceDeviceId = (): string | undefined => {
  try {
    const existing = localStorage.getItem(ATTENDANCE_DEVICE_ID_KEY)?.trim();
    if (existing) return existing;

    const id = createAttendanceDeviceId();
    localStorage.setItem(ATTENDANCE_DEVICE_ID_KEY, id);
    return id;
  } catch {
    return undefined;
  }
};

export const buildWebAttendanceMetadata = (): AttendanceClientMetadata => {
  try {
    return {
      source: 'web_pos',
      platform: navigator?.platform || 'web',
      deviceId: getAttendanceDeviceId(),
      language: navigator?.language,
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      screenWidth: screen?.width,
      screenHeight: screen?.height
    };
  } catch {
    return {
      source: 'web_pos',
      platform: 'web'
    };
  }
};

export const getAttendanceLocationMetadata = async (): Promise<AttendanceLocationMetadata> => {
  const capturedAt = new Date().toISOString();

  try {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      return {
        permissionStatus: 'unavailable',
        capturedAt
      };
    }

    return await new Promise<AttendanceLocationMetadata>((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { coords } = position;
          resolve(omitUndefined({
            permissionStatus: 'granted',
            latitude: coords.latitude,
            longitude: coords.longitude,
            accuracy: coords.accuracy,
            altitude: optionalNumber(coords.altitude),
            altitudeAccuracy: optionalNumber(coords.altitudeAccuracy),
            heading: optionalNumber(coords.heading),
            speed: optionalNumber(coords.speed),
            capturedAt: new Date(position.timestamp || Date.now()).toISOString()
          }));
        },
        (error) => {
          resolve({
            permissionStatus: error.code === 1 ? 'denied' : 'error',
            capturedAt: new Date().toISOString(),
            errorCode: String(error.code),
            errorMessage: error.message
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 8000,
          maximumAge: 60000
        }
      );
    });
  } catch (error: any) {
    return {
      permissionStatus: 'error',
      capturedAt,
      errorCode: error?.name || 'geolocation_error',
      errorMessage: error?.message || 'No se pudo obtener la ubicación'
    };
  }
};

const withWebAttendanceMetadata = async <T extends { metadata?: AttendanceClientMetadata }>(
  data: T
): Promise<T> => {
  const location = await getAttendanceLocationMetadata();

  return {
    ...data,
    metadata: {
      ...buildWebAttendanceMetadata(),
      ...data.metadata,
      location,
      source: 'web_pos'
    }
  };
};

export const getAttendanceHistoryV2 = async (params: {
  storeId?: string;
  userId?: string;
  startDate?: string;
  endDate?: string;
  pageNum?: string;
  limit?: string;
}) => {
  try {
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== undefined)
    );
    return await WashdayClientSingleton.getInstance().attendance.getHistory(filteredParams);
  } catch (e) {
    console.log('ERROR getAttendanceHistoryV2', e);
    throw e;
  }
};

export const getAttendanceStatusV2 = async (params: {
  storeId?: string;
  userId?: string;
}) => {
  try {
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== undefined)
    );
    return await WashdayClientSingleton.getInstance().attendance.getStatus(filteredParams);
  } catch (e) {
    console.log('ERROR getAttendanceStatusV2', e);
    throw e;
  }
};

export const getStoreAttendanceReportV2 = async (storeId: string, params: {
  startDate?: string;
  endDate?: string;
  pageNum?: string;
  limit?: string;
}) => {
  try {
    const filteredParams = Object.fromEntries(
      Object.entries(params).filter(([_, value]) => value !== undefined)
    );
    return await WashdayClientSingleton.getInstance().attendance.getStoreReport(storeId, filteredParams);
  } catch (e) {
    console.log('ERROR getStoreAttendanceReportV2', e);
    throw e;
  }
};

export const clockInV2 = async (data: {
  storeId: string;
  userId?: string;
  notes?: string;
  metadata?: AttendanceClientMetadata;
}) => {
  try {
    return await WashdayClientSingleton.getInstance().attendance.clockIn(
      await withWebAttendanceMetadata(data)
    );
  } catch (e) {
    console.log('ERROR clockInV2', e);
    throw e;
  }
};

export const clockOutV2 = async (data: {
  userId?: string;
  storeId?: string;  // ✅ AÑADIDO - requerido por backend
  notes?: string;
  metadata?: AttendanceClientMetadata;
}) => {
  try {
    return await WashdayClientSingleton.getInstance().attendance.clockOut(
      await withWebAttendanceMetadata(data)
    );
  } catch (e) {
    console.log('ERROR clockOutV2', e);
    throw e;
  }
};

export const updateAttendanceEntryV2 = async (id: string, data: {
  timestamp?: string;
  notes?: string;
  reason: string;
}) => {
  try {
    return await WashdayClientSingleton.getInstance().attendance.updateById(id, data);
  } catch (e) {
    console.log('ERROR updateAttendanceEntryV2', e);
    throw e;
  }
};
